import { useState, type FormEvent, type ReactNode } from 'react'
import type { PlayerPosition } from '../../types'
import Field from '../forms/Field'

export interface PlayerFormValues {
  name: string
  position: PlayerPosition | ''
  nationality: string
  university: string
  dob: string
  scouting_narrative: string
  // Phase A1 — agent-supplied API-Sports player id. String at the form
  // level (input is type="number" but value semantics are textual);
  // the server-side handler parses to integer or null.
  api_sports_player_id: string
}

export const emptyPlayerForm: PlayerFormValues = {
  name: '',
  position: '',
  nationality: '',
  university: '',
  dob: '',
  scouting_narrative: '',
  api_sports_player_id: '',
}

const POSITIONS: PlayerPosition[] = ['PG', 'SG', 'SF', 'PF', 'C']

interface Props {
  initial?: PlayerFormValues
  submitLabel: string
  onSubmit: (values: PlayerFormValues) => Promise<void>
  onCancel?: () => void
  // Extra fields rendered inside the same <form>, above the submit button,
  // so the single "Save changes" persists them too. PlayerEdit passes the
  // socials + films sections here; PlayerNew passes none.
  children?: ReactNode
}

export default function PlayerForm({ initial, submitLabel, onSubmit, onCancel, children }: Props) {
  const [form, setForm] = useState<PlayerFormValues>(initial ?? emptyPlayerForm)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function update<K extends keyof PlayerFormValues>(key: K, value: PlayerFormValues[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handle(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setErr(null)
    setSubmitting(true)
    try {
      await onSubmit(form)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
    } finally {
      // Always re-enable the button — even on success. Earlier versions
      // assumed the parent's onSubmit would navigate away on success
      // (PlayerNew still does), which made the button visually stuck
      // briefly. A1 changed PlayerEdit's onSubmit to STAY on the page,
      // which made the "Saving…" label persist forever. The finally
      // here handles both surfaces cleanly — if the component does
      // unmount due to navigation, setState on an unmounted component
      // is a React 19 no-op.
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handle} className="space-y-5">
      <Field label="Player name" required>
        <input
          required
          type="text"
          value={form.name}
          onChange={e => update('name', e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="Position" required>
        <select
          required
          value={form.position}
          onChange={e => update('position', e.target.value as PlayerPosition | '')}
          className={inputClass}
        >
          <option value="">— select —</option>
          {POSITIONS.map(p => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Nationality" required hint="Country name or ISO code.">
        <input
          required
          type="text"
          value={form.nationality}
          onChange={e => update('nationality', e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="University">
        <input
          type="text"
          value={form.university}
          onChange={e => update('university', e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="Date of birth">
        <input
          type="date"
          value={form.dob}
          onChange={e => update('dob', e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field label="Scouting narrative" hint="Agent Verified — your long-form take.">
        <textarea
          rows={5}
          value={form.scouting_narrative}
          onChange={e => update('scouting_narrative', e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field
        label="API-Sports Player ID"
        hint="Optional. Find at api-sports.io. Enables auto-synced stats for Thailand TBL and Vietnam VBA players."
      >
        <input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={form.api_sports_player_id}
          onChange={e => update('api_sports_player_id', e.target.value)}
          className={inputClass}
        />
      </Field>

      {children}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-hoop-text-2 hover:text-hoop-text text-sm"
          >
            Cancel
          </button>
        )}
        {err && (
          <p className="text-sm text-hoop-red">{err}</p>
        )}
      </div>
    </form>
  )
}

const inputClass =
  'w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent'
